import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, Leaf, Sprout } from "lucide-react";
import {
  getCurrentWhatsAppGroupCode,
  getCurrentWhatsAppGroupUrl,
} from "@/lib/whatsapp-groups";

export const Route = createFileRoute("/obrigado-grupo")({
  head: () => ({
    meta: [
      { title: "Abrindo grupo — Cura pela Alimentação" },
      {
        name: "description",
        content: "Acesso ao grupo de WhatsApp da masterclass Cura pela Alimentação.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ObrigadoGrupo,
});

function ObrigadoGrupo() {
  const [attemptCount, setAttemptCount] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibilityHandlerRef = useRef<(() => void) | null>(null);

  const clearPendingDetection = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (visibilityHandlerRef.current) {
      document.removeEventListener("visibilitychange", visibilityHandlerRef.current);
      visibilityHandlerRef.current = null;
    }
  }, []);

  const openWhatsAppGroup = useCallback(() => {
    clearPendingDetection();

    const groupCode = getCurrentWhatsAppGroupCode();
    const webLink = getCurrentWhatsAppGroupUrl();
    const isMobile = /Android|iPad|iPhone|iPod/i.test(navigator.userAgent);

    if (!isMobile) {
      window.location.assign(webLink);
      return;
    }

    const currentAttempt = attemptCount + 1;
    setAttemptCount(currentAttempt);
    let appOpened = false;

    const handleVisibilityChange = () => {
      if (document.hidden) appOpened = true;
    };

    visibilityHandlerRef.current = handleVisibilityChange;
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.location.assign(`whatsapp://chat?code=${groupCode}`);

    timeoutRef.current = setTimeout(() => {
      clearPendingDetection();
      if (!appOpened && currentAttempt >= 2) {
        window.location.assign(webLink);
      }
    }, 3000);
  }, [attemptCount, clearPendingDetection]);

  useEffect(() => {
    openWhatsAppGroup();
    return clearPendingDetection;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="relative flex min-h-screen items-start justify-center overflow-hidden px-4 pt-3 pb-6 sm:items-center sm:py-10">
      {/* ambient glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-leaf/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[320px] w-[320px] rounded-full bg-gold/10 blur-3xl" />
      </div>

      <article
        className="relative z-10 w-full max-w-[440px] overflow-hidden rounded-3xl bg-card px-6 py-10 text-center sm:px-8 sm:py-12"
        style={{ boxShadow: "var(--shadow-card)" }}
      >
        <Leaf
          className="absolute left-5 top-5 size-6 text-leaf-bright/60 float-soft"
          strokeWidth={1.5}
        />
        <Sprout
          className="absolute right-5 top-7 size-6 text-lime/60 float-soft"
          style={{ animationDelay: "1.2s" }}
          strokeWidth={1.5}
        />

        <div className="mx-auto flex size-20 items-center justify-center rounded-full border border-leaf/40 bg-leaf/10 backdrop-blur-sm">
          <MessageCircle
            className="size-10 animate-pulse text-leaf-bright"
            strokeWidth={2.2}
          />
        </div>

        <h1 className="mt-6 font-display text-3xl font-bold uppercase leading-[1.05] text-cream sm:text-4xl">
          {attemptCount === 0 ? "Abrindo WhatsApp..." : "WhatsApp não abriu?"}
        </h1>

        <p className="mx-auto mt-5 max-w-[340px] text-[15px] font-semibold leading-relaxed text-leaf-bright">
          {attemptCount === 0
            ? "Estamos abrindo o grupo correto para você."
            : "Clique no botão abaixo para tentar novamente."}
        </p>

        <button
          type="button"
          onClick={openWhatsAppGroup}
          className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-2xl px-5 py-5 text-base font-extrabold uppercase tracking-wider text-white transition-all duration-300 hover:scale-[1.03] hover:brightness-110 active:scale-[0.99]"
          style={{
            background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
          }}
        >
          <MessageCircle className="size-6" strokeWidth={2.5} />
          Abrir WhatsApp
        </button>

        <p className="mt-5 text-[11px] leading-relaxed text-muted-foreground">
          Se o aplicativo não abrir automaticamente, use o botão acima.
        </p>
      </article>
    </main>
  );
}
